import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {RatingUnitService} from '../../services/rating-unit.service';
import {MatPaginator} from '@angular/material/paginator';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {RatingUnit} from '../../model/rating-unit';
import {HttpErrorResponse} from '@angular/common/http';
import {StringUtils} from '../../utils/string-utils';
import {Router} from '@angular/router';
import {SelectionModel} from '@angular/cdk/collections';
import {firstValueFrom, forkJoin, Observable, Subject} from 'rxjs';
import {CdkDrag, CdkDragDrop, CdkDropList} from '@angular/cdk/drag-drop';
import {Entity} from '../../entity/entity';
import {ScheduleService} from '../../services/schedule.service';
import {RatingScheduleLineService} from '../../services/rating-schedule-line.service';
import {RatingsMassUpdate} from '../../model/ratings-mass-update';
import {RatingService} from '../../services/rating.service';
import {UnitRatingResponse} from '../../model/unit-rating-response';
import {UnitRatingRequest} from '../../model/unit-rating-request';
import {SelectionItem} from '../../selections/selection-item';
import {PersonService} from '../../services/person-service';
import {SelectionService} from '../../services/selection-service';
import {MatTooltip} from '@angular/material/tooltip';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {DialogService} from "../../services/dialog.service";

@Component({
    selector: 'app-unit-list',
    templateUrl: './unit-list.component.html',
    styleUrls: ['./unit-list.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, MatProgressSpinner, MatPaginator, MatTable, CdkDropList, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatTooltip, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, CdkDrag]
})
export class UnitListComponent implements OnInit {
  private readonly ratingUnitService = inject(RatingUnitService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly scheduleService = inject(ScheduleService);
  private readonly ratingScheduleLineService = inject(RatingScheduleLineService);
  private readonly ratingService = inject(RatingService);
  private readonly personService = inject(PersonService);
  private readonly selectionService = inject(SelectionService);


  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  readonly datasource: MatTableDataSource<RatingUnit> = new MatTableDataSource<RatingUnit>();
  readonly selection = signal<SelectionModel<RatingUnit>>(new SelectionModel<RatingUnit>(true, []));
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly displayedColumns = signal<string[]>([]);

  ngOnInit(): void {
    this.isBusy.set(true);
    this.displayedColumns.set(['select', 'edit', 'sequence']);
    this.personService.getRatingScheduleColumns().subscribe({
      next: (columns) => {
        for (const column of columns) {
          this.displayedColumns.update(current => [...current, column.columnName]);
        }
        this.displayedColumns.update(current => [...current, 'notes']);
        this.loadUnitList([]);
      }, error: (error) => this.handleError(error)
    });
  }

  private loadUnitList(selectedIds: number[]): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.ratingUnitService.getUnits().subscribe({
      next: (units) => {
        this.datasource.data = units;
        this.datasource.paginator = this.paginator;
        this.selection.set(new SelectionModel<RatingUnit>(true, []));
        if ((selectedIds?.length || 0) > 0) {
          const selected = this.datasource.data.filter(unit => selectedIds.indexOf(unit.id) >= 0);
          this.selection().select(...selected);
        }
        this.isBusy.set(false);
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  editUnit(unit: RatingUnit): void {
    this.router.navigate(['rating', 'unitDetail', unit.id]);
  }

  masterToggle(): void {
    this.isAllSelected() ?
      this.datasource.filteredData.forEach(row => this.selection().deselect(row)) :
      this.datasource.filteredData.forEach(row => this.selection().select(row));
  }

  isAllSelected(): boolean {
    return (this.selection().selected?.length || 0) === (this.datasource.filteredData?.length || 0);
  }

  selectionSize(): number {
    return (this.selection().selected?.length || 0);
  }

  unitsSelected(): boolean {
    return this.selectionSize() > 0;
  }

  toggleRowSelection(row: RatingUnit): void {
    this.selection().toggle(row);
  }

  copySelectedUnits(): void {
    this.isBusy.set(false);
    this.errorMessage.set(undefined!);
    if (this.unitsSelected()) {
      this.isBusy.set(true);
      const sorted = this.selection().selected.sort((first, second) => first.sequence - second.sequence);
      this.getCopiedIds(sorted).then(copiedIds => {
        this.loadUnitList(copiedIds);
      });
    }
  }

  private async getCopiedIds(units: RatingUnit[]): Promise<number[]> {
    const copiedIds: number[] = [];
    for (const unit of units) {
      await this.copyUnit(unit).then(copied => copiedIds.push(copied.id));
    }
    return copiedIds;
  }

  private async copyUnit(unit: RatingUnit): Promise<RatingUnit> {
    return await firstValueFrom<RatingUnit>(this.copy(unit));
  }

  private copy(unit: RatingUnit): Observable<RatingUnit> {
    const copy = new RatingUnit();
    copy.personId = unit.personId;
    copy.orgUnitId = unit.orgUnitId;
    copy.productId = unit.productId;
    copy.modelCode = unit.modelCode;
    copy.sizeCode = unit.sizeCode;
    copy.systemTypeCode = unit.systemTypeCode;
    copy.coilRowCode = unit.coilRowCode;
    copy.powerSupplyCode = unit.powerSupplyCode;
    copy.motorTypeCode = unit.motorTypeCode;
    copy.airFilterCode = unit.airFilterCode;
    copy.electricHeatCode = unit.electricHeatCode;
    copy.request = unit.request;
    copy.response = unit.response;
    return this.ratingUnitService.saveUnit(copy);
  }

  deleteSelectedUnits(): void {
    this.isBusy.set(false);
    this.errorMessage.set(undefined!);
    if (this.unitsSelected()) {
      this.isBusy.set(true);
      const deleteList: Observable<string>[] = [];
      for (const unit of this.selection().selected) {
        deleteList.push(this.delete(unit));
      }
      forkJoin(deleteList).subscribe({
        complete: () => this.loadUnitList([]),
        error: (error) => this.handleError(error)
      });
    }
  }

  private delete(unit: RatingUnit): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    this.ratingUnitService.deleteUnit(unit.id).subscribe({
      next: (response) => subject.next(response),
      complete: () => subject.complete(),
      error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  moveSelectedUnitsUp(): void {
    if (this.canMoveUp()) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      const sorted = this.selection().selected.sort((first, second) => first.sequence - second.sequence);
      this.moveUnitsUp(sorted).then((ids) => this.loadUnitList(ids), (error) => this.handleError(error));
    }
  }

  private async moveUnitsUp(units: RatingUnit[]): Promise<number[]> {
    const ids: number[] = [];
    for (const unit of units) {
      await this.changeUnitSequence(unit.id, (unit.sequence - 1).toString());
      ids.push(unit.id);
    }
    return ids;
  }

  canMoveUp(): boolean {
    if (this.isBusy()) {
      return false;
    }
    if (!this.unitsSelected()) {
      return false;
    }
    const minSequence = Math.min(...this.selection().selected.map(ru => ru.sequence));
    return minSequence > 1;
  }

  moveSelectedUnitsDown(): void {
    if (this.canMoveDown()) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      const sorted = this.selection().selected.sort((first, second) => second.sequence - first.sequence);
      this.moveUnitsDown(sorted).then((ids) => this.loadUnitList(ids), (error) => this.handleError(error));
    }
  }

  private async moveUnitsDown(units: RatingUnit[]): Promise<number[]> {
    const ids: number[] = [];
    for (const unit of units) {
      await this.changeUnitSequence(unit.id, (unit.sequence + 1).toString());
      ids.push(unit.id);
    }
    return ids;
  }

  private async changeUnitSequence(id: number, newSequence: string): Promise<string> {
    return await firstValueFrom<string>(this.ratingUnitService.changeUnitSequence(id, newSequence));
  }

  canMoveDown(): boolean {
    if (this.isBusy()) {
      return false;
    }
    if (!this.unitsSelected()) {
      return false;
    }
    const maxSelectedSequence = Math.max(...this.selection().selected.map(ru => ru.sequence));
    const maxSequence = this.getMaxSequence();
    return maxSelectedSequence < maxSequence;
  }


  private getMaxSequence(): number {
    return Math.max(...this.datasource.data.map(ru => ru.sequence));
  }

  onListDrop(event: CdkDragDrop<RatingUnit[]>): void {
    const selectedItem: RatingUnit = JSON.parse(JSON.stringify(this.datasource.data[event.previousIndex]));
    const movedItem: RatingUnit = JSON.parse(JSON.stringify(this.datasource.data[event.currentIndex]));
    if (selectedItem.sequence !== movedItem.sequence) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      this.ratingUnitService.changeUnitSequence(selectedItem.id, movedItem.sequence.toString()).subscribe({
        next: () => this.loadUnitList([selectedItem.id]),
        error: (error) => this.handleError(error),
        complete: () => this.isBusy.set(false)
      });
    }
  }

  createSchedule(): void {
    this.errorMessage.set(undefined!);
    if (this.unitsSelected()) {
      this.dialogService.showAddScheduleDialog().subscribe({
        next: (input: Entity) => {
          if (input && input.projectName.trim().length) {
            this.isBusy.set(true);
            this.scheduleService.createSchedule(input).subscribe({
              next: (schedule) => {
                this.addScheduleLines(schedule).then(() => this.router.navigate(['schedules', schedule.id.entityId]), (error) => this.handleError(error));
              },
              error: (error) => this.handleError(error),
            });
          }
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private async addScheduleLines(schedule: Entity): Promise<void> {
    for (const unit of this.selection().selected) {
      await this.addLineToSchedule(schedule, unit);
    }
  }

  private async addLineToSchedule(schedule: Entity, unit: RatingUnit): Promise<string> {
    return await firstValueFrom<string>(this.ratingScheduleLineService.addScheduleLine(schedule, unit, [], 1));
  }

  openMassUpdateDialog(): void {
    if (this.unitsSelected()) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      const updateData: RatingsMassUpdate = this.buildMassUpdateData();
      this.dialogService.showRatingsMassUpdateDialog(updateData).subscribe({
        next: (response: RatingsMassUpdate) => {
          if (response) {
            const selectedIds: number[] = this.selection().selected.map(unit => unit.id);
            this.updateMassUpdateRatingResponses(response).then(() => {
              const updates: Observable<RatingUnit>[] = this.saveSelectedUnits();
              const errorUnits: RatingUnit[] = [];
              forkJoin(updates).subscribe({
                next: (units) => {
                  for (const unit of units) {
                    if (StringUtils.isZeroOrEmpty(unit?.response?.Airflow)) {
                      errorUnits.push(unit);
                    }
                  }
                }, complete: () => {
                  if (errorUnits.length > 0) {
                    this.showMassUpdateErrorsDialog(errorUnits, selectedIds);
                  } else {
                    this.loadUnitList(selectedIds);
                  }
                }, error: (error) => this.handleError(error)
              });
            });
          } else {
            this.isBusy.set(false);
          }
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private showMassUpdateErrorsDialog(errorUnits: RatingUnit[], selectedIds: number[]): void {
    this.dialogService.showRatingMassUpdateErrorsDialog(errorUnits, null!).subscribe({
      next: () => this.loadUnitList(selectedIds),
      error: (error) => this.handleError(error),
    });
  }

  private saveSelectedUnits(): Observable<RatingUnit>[] {
    const requests: Observable<RatingUnit>[] = [];
    for (const unit of this.selection().selected) {
      requests.push(this.ratingUnitService.saveUnit(unit));
    }
    return requests;
  }

  private async updateMassUpdateRatingResponses(response: RatingsMassUpdate): Promise<string> {
    const updatedUnits: Set<RatingUnit> = new Set<RatingUnit>();
    for (const id of response.hydronicCoolingLines) {
      const unit = this.getMassUpdateUnit(id, updatedUnits, response);
      unit.request.PrimaryCoilRequest.EnteringDryBulb = response.hydronicCoolingInputs.EnteringDryBulb;
      unit.request.PrimaryCoilRequest.EnteringWetBulb = response.hydronicCoolingInputs.EnteringWetBulb;
      unit.request.PrimaryCoilRequest.EnteringFluidTemperature = response.hydronicCoolingInputs.EnteringFluidTemperature;
      unit.request.PrimaryCoilRequest.FluidType = response.hydronicCoolingInputs.FluidType;
      unit.request.PrimaryCoilRequest.GlycolPercentage = response.hydronicCoolingInputs.GlycolPercentage;
      unit.request.PrimaryCoilRequest.TemperatureChange = response.hydronicCoolingInputs.TemperatureChange;
      unit.request.PrimaryCoilRequest.FlowRate = response.hydronicCoolingInputs.FlowRate;
      updatedUnits.add(unit);
    }
    for (const id of response.hydronicHeatingLines) {
      const unit = this.getMassUpdateUnit(id, updatedUnits, response);
      if (unit.request.SecondaryCoilRequest) {
        unit.request.SecondaryCoilRequest.EnteringDryBulb = response.hydronicHeatingInputs.EnteringDryBulb;
        unit.request.SecondaryCoilRequest.EnteringFluidTemperature = response.hydronicHeatingInputs.EnteringFluidTemperature;
        unit.request.SecondaryCoilRequest.FluidType = response.hydronicHeatingInputs.FluidType;
        unit.request.SecondaryCoilRequest.GlycolPercentage = response.hydronicHeatingInputs.GlycolPercentage;
        unit.request.SecondaryCoilRequest.TemperatureChange = response.hydronicHeatingInputs.TemperatureChange;
        unit.request.SecondaryCoilRequest.FlowRate = response.hydronicHeatingInputs.FlowRate;
      } else {
        unit.request.PrimaryCoilRequest.EnteringDryBulb = response.hydronicHeatingInputs.EnteringDryBulb;
        unit.request.PrimaryCoilRequest.EnteringFluidTemperature = response.hydronicHeatingInputs.EnteringFluidTemperature;
        unit.request.PrimaryCoilRequest.FluidType = response.hydronicHeatingInputs.FluidType;
        unit.request.PrimaryCoilRequest.GlycolPercentage = response.hydronicHeatingInputs.GlycolPercentage;
        unit.request.PrimaryCoilRequest.TemperatureChange = response.hydronicHeatingInputs.TemperatureChange;
        unit.request.PrimaryCoilRequest.FlowRate = response.hydronicHeatingInputs.FlowRate;
      }
      updatedUnits.add(unit);
    }
    for (const id of response.refrigerantLines) {
      const unit = this.getMassUpdateUnit(id, updatedUnits, response);
      unit.request.PrimaryCoilRequest.EnteringDryBulb = response.refrigerantInputs.EnteringDryBulb;
      unit.request.PrimaryCoilRequest.EnteringWetBulb = response.refrigerantInputs.EnteringWetBulb;
      unit.request.PrimaryCoilRequest.SaturationSuctionTemperature = response.refrigerantInputs.SaturationSuctionTemperature;
      updatedUnits.add(unit);
    }
    for (const id of response.steamLines) {
      const unit = this.getMassUpdateUnit(id, updatedUnits, response);
      if (unit.request.SecondaryCoilRequest) {
        unit.request.SecondaryCoilRequest.EnteringDryBulb = response.steamInputs.EnteringDryBulb;
        unit.request.SecondaryCoilRequest.EnteringSteamPressure = response.steamInputs.EnteringSteamPressure;
      } else {
        unit.request.PrimaryCoilRequest.EnteringDryBulb = response.steamInputs.EnteringDryBulb;
        unit.request.PrimaryCoilRequest.EnteringSteamPressure = response.steamInputs.EnteringSteamPressure;
      }
      updatedUnits.add(unit);
    }
    for (const id of response.electricHeatLines) {
      const unit = this.getMassUpdateUnit(id, updatedUnits, response);
      unit.request.ElectricHeatRequest.EnteringDryBulb = response.electricHeatInputs.EnteringDryBulb;
      updatedUnits.add(unit);
    }
    for (const unit of updatedUnits) {
      await this.updateLineRatings(unit.request).then(response => unit.response = response);
    }
    return 'Finished';
  }

  private getMassUpdateUnit(id: number, updatedUnits: Set<RatingUnit>, response: RatingsMassUpdate): RatingUnit {
    const existingUpdate = [...updatedUnits].filter(unit => unit.id === id);
    let unit: RatingUnit;
    if (existingUpdate.length === 1) {
      unit = existingUpdate[0];
    } else {
      unit = this.selection().selected.filter(ru => ru.id === id)[0];
      unit.request.ExternalStaticPressure = response.externalStaticPressure;
      unit.request.Altitude = response.altitude;
      unit.request.IsMetricInput = response.isMetric;
    }
    return unit;
  }

  private async updateLineRatings(request: UnitRatingRequest): Promise<UnitRatingResponse> {
    return await firstValueFrom<UnitRatingResponse>(this.ratingService.getUnitRatingResponse(request));
  }

  private buildMassUpdateData(): RatingsMassUpdate {
    const updateData: RatingsMassUpdate = new RatingsMassUpdate();
    for (const unit of this.selection().selected) {
      updateData.isMetric = unit.request.IsMetricInput;
      updateData.externalStaticPressure = unit.request.ExternalStaticPressure;
      updateData.altitude = unit.request.Altitude;
      if (!StringUtils.isEmptyNumericValue(unit.request.PrimaryCoilRequest?.SaturationSuctionTemperature)) {
        updateData.refrigerantLines.push(unit.id);
        updateData.refrigerantInputs = JSON.parse(JSON.stringify(unit.request.PrimaryCoilRequest));
        updateData.refrigerantInputs.FluidType = undefined!;
        updateData.refrigerantInputs.isRefrigerant = true;
        updateData.refrigerantInputs.isCooling = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.request.PrimaryCoilRequest?.EnteringSteamPressure)) {
        updateData.steamLines.push(unit.id);
        updateData.steamInputs = JSON.parse(JSON.stringify(unit.request.PrimaryCoilRequest));
        updateData.steamInputs.FluidType = undefined!;
        updateData.steamInputs.isSteam = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.request.PrimaryCoilRequest?.EnteringFluidTemperature)) {
        if (unit.request.PrimaryCoilRequest.EnteringFluidTemperature > unit.request.PrimaryCoilRequest.EnteringDryBulb) {
          updateData.hydronicHeatingLines.push(unit.id);
          updateData.hydronicHeatingInputs = JSON.parse(JSON.stringify(unit.request.PrimaryCoilRequest));
          updateData.hydronicHeatingInputs.isFluid = true;
        } else {
          updateData.hydronicCoolingLines.push(unit.id);
          updateData.hydronicCoolingInputs = JSON.parse(JSON.stringify(unit.request.PrimaryCoilRequest));
          updateData.hydronicCoolingInputs.isFluid = true;
          updateData.hydronicCoolingInputs.isCooling = true;
        }
      }
      if (!StringUtils.isEmptyNumericValue(unit.request.SecondaryCoilRequest?.EnteringSteamPressure)) {
        updateData.steamLines.push(unit.id);
        updateData.steamInputs = JSON.parse(JSON.stringify(unit.request.SecondaryCoilRequest));
        updateData.steamInputs.isSteam = true;
      } else if (!StringUtils.isEmptyNumericValue(unit.request.SecondaryCoilRequest?.EnteringFluidTemperature)) {
        updateData.hydronicHeatingLines.push(unit.id);
        updateData.hydronicHeatingInputs = JSON.parse(JSON.stringify(unit.request.SecondaryCoilRequest));
        updateData.hydronicHeatingInputs.isFluid = true;
      }
      if (!StringUtils.isEmptyNumericValue(unit.request.ElectricHeatRequest?.EnteringDryBulb)) {
        updateData.electricHeatLines.push(unit.id);
        updateData.electricHeatInputs = JSON.parse(JSON.stringify(unit.request.ElectricHeatRequest));
      }
    }
    return updateData;
  }

  chooseColumns(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const selectedColumns = this.displayedColumns().slice(3);
    selectedColumns.splice(selectedColumns.indexOf('notes'), 1);
    this.selectionService.getRatingScheduleColumns().subscribe({
      next: (allColumns: SelectionItem[]) => {
        this.dialogService.showColumnSelectionDialog(allColumns, selectedColumns).subscribe({
          next: (columnNames: string[]) => {
            if (columnNames) {
              this.displayedColumns.set(['select', 'edit', 'sequence']);
              for (const columnName of columnNames) {
                this.displayedColumns.update(current => [...current, columnName]);
              }
              this.displayedColumns.update(current => [...current, 'notes']);
              this.personService.saveRatingScheduleColumns(columnNames).subscribe({
                next: () => this.isBusy.set(false),
                error: (error) => this.handleError(error)
              });
            } else {
              this.isBusy.set(false);
            }
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

}
